import { ConfirmPasswordView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ConfirmPasswordView";
import { CreatePasswordView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/CreatePasswordView";
import {
  EmailSentScenario,
  AutoEmailSentWaitingView,
  MFAOptionsEmailSentWaitingView,
} from "@faire/retailer-visitor-shared/components/AppSignIn/components/EmailSentWaitingView";
import { EnterCodeView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EnterCodeView";
import { EnterEmailView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EnterEmailView";
import { EnterMFACodeView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EnterMFACodeView";
import { EnterMFAView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EnterMFAView";
import { EnterPasswordView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/EnterPasswordView";
import { LoadingView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/LoadingView";
import { ResetPasswordView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/ResetPasswordView";
import { SelectMFAOptionView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SelectMFAOptionView";
import { SSOView } from "@faire/retailer-visitor-shared/components/AppSignIn/components/SSOView";
import { View } from "@faire/retailer-visitor-shared/components/AppSignIn/components/View";
import { useSignInEventTracker } from "@faire/retailer-visitor-shared/components/AppSignIn/useSignInEventTracker";
import { useHCaptchaLogin } from "@faire/retailer-visitor-shared/hooks/hCaptcha/useHCaptchaLogin";
import { getPayloadForConfirmPassword } from "@faire/retailer-visitor-shared/lib/ssoUtils";
import { PageLoadEvent } from "@faire/retailer-visitor-shared/services/performance/const";
import { PerformanceMarker } from "@faire/retailer-visitor-shared/services/performance/PerformanceMarker";
import { useSSOMFAStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOMFAStore";
import { useSSOPasswordStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOPasswordStore";
import { useSSOStore } from "@faire/retailer-visitor-shared/stores/domain/SSO/useSSOStore";
import { IImage } from "@faire/web-api--source/indigofair/data/IImage";
import { ILoginResponse } from "@faire/web-api--source/indigofair/data/ILoginResponse";
import { IUser } from "@faire/web-api--source/indigofair/data/IUser";
import { MfaMethod } from "@faire/web-api--source/indigofair/data/MfaMethod";
import { useStrictLocalization } from "@faire/web/common/localization";
import { logAssert } from "@faire/web/common/logging";
import { trackEvent } from "@faire/web/common/trackEvent";
import { isNotUndefined, isNullish } from "@faire/web/common/typescriptUtils";
import pickBy from "lodash/pickBy";
import React, { FC, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useHistory } from "react-router-dom";

interface IProps {
  onLogin: (user: IUser) => void;
  signInTitle?: string;
  email?: string;
  sendResetEmail?: boolean;
  hideApple?: boolean;
  isResetPasswordSuccess?: boolean;
}

interface IState {
  view: View;
  email: string;
  mfaDescription?: string;
  mfaTitle?: string;
  password: string;
  loggedInUser: IUser | null;
  userToken?: string;
  profileImage?: IImage;
  userName?: string;
  mfaOptions?: ILoginResponse.IMfaOption[];
  mfaMethod?: keyof typeof MfaMethod;
  isMFAFromEmail?: boolean;
  emailSentScenario?: EmailSentScenario;
}

export type AllResult = Partial<IState> & {
  nextView: View;
  user?: IUser | null;
};

export const SignInController: FC<IProps> = (props) => {
  const {
    onLogin,
    sendResetEmail,
    signInTitle,
    hideApple,
    isResetPasswordSuccess,
  } = props;
  // email can't be destructured due to const name conflicts
  const location = useLocation();
  const history = useHistory();
  const { strictLocalize } = useStrictLocalization();

  const signInTitleEmailView = strictLocalize({
    id: "3aJgDA",
    defaultMessage: "Sign in to Faire",
    description: {
      text: "Title that welcomes the user back to the site.",
      img: "https://cdn.faire.com/fastly/0072fcb175c20e48165547f439b4aff79c07578eca4c057ce51e974b28ea9702.png",
    },
  });

  const signInTitlePasswordView = strictLocalize({
    id: "CAZpRL",
    defaultMessage: "Welcome back",
    description: {
      text: "Title that welcomes the user back to the site.",
      img: "https://cdn.faire.com/fastly/90182d874d2c3b5b3db7edf81e2f76daac27a669af9a1eecc44eb385627ccb50.png",
    },
  });

  const [state, setStateBase] = useState<IState>({
    view: View.ENTER_EMAIL,
    email: props.email ?? "",
    password: "",
    loggedInUser: null,
    profileImage: IImage.build(),
  });

  useHCaptchaLogin();

  // Partially set state
  const setState = (newState: Partial<IState>) => {
    // Prefer original state value if new value is undefined or null
    const filteredState: Partial<IState> = pickBy(
      newState,
      (val) => !isNullish(val)
    );
    setStateBase((prevState) => ({ ...prevState, ...filteredState }));
  };

  const onComplete = (result: AllResult) => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has("error")) {
      searchParams.delete("error");
    }
    const newQueryString = searchParams.toString();
    const newUrl = newQueryString
      ? `${location.pathname}?${newQueryString}`
      : location.pathname;
    history.replace(newUrl);
    return setState({
      // Some of these components return user while others return loggedInUser
      ...pickBy(
        { view: result.nextView, loggedInUser: result.user },
        (val) => !isNullish(val)
      ),
      ...result,
    });
  };

  const { promptSSO, signUpSuggestions } = useSSOStore();

  const {
    showSelectMFA,
    mfaOptions: ssoMfaOptions,
    mfaTitle: ssoMfaTitle,
    mfaDescription: ssoMfaDescription,
    resetMFADescription,
    resetMFATitle,
    resetMFAOptions,
  } = useSSOMFAStore();

  const { showConfirmPasswordView, userHasPassword } = useSSOPasswordStore();

  // mfaDescription, mfaTitle and mfaOptions cannot be destructured from ssoStore because they are also state variables

  const { trackView } = useSignInEventTracker(state.view);

  useEffect(() => {
    trackEvent("/login-modal");
    PerformanceMarker.get().mark(PageLoadEvent.firstMeaningfulPaint);
    trackView();
  }, [trackView]);

  // The following are triggered when sign in via SSO in the AppSignUp
  // needs an additonal step that utilises a view in SignInController
  useEffect(() => {
    if (promptSSO) {
      if (userHasPassword) {
        setState({
          view: View.SSO,
        });
      } else {
        setState({
          view: View.ENTER_PASSWORD,
        });
      }
    }
  }, [promptSSO, userHasPassword]);

  useEffect(() => {
    if (showSelectMFA) {
      setState({
        view: View.SELECT_MFA_OPTION,
        mfaOptions: ssoMfaOptions,
      });
    }
  }, [showSelectMFA, ssoMfaOptions]);

  useEffect(() => {
    if (ssoMfaDescription) {
      setState({
        view: View.ENTER_MFA,
        mfaDescription: ssoMfaDescription,
        mfaTitle: ssoMfaTitle,
      });
    }
  }, [ssoMfaDescription, ssoMfaTitle]);

  useEffect(() => {
    if (showConfirmPasswordView) {
      const { email, userName, profileImage } =
        getPayloadForConfirmPassword(signUpSuggestions);
      setState({
        view: View.CONFIRM_PASSWORD,
        email: email,
        userName: userName,
        profileImage: profileImage,
      });
    }
  }, [showConfirmPasswordView, signUpSuggestions]);

  const {
    view,
    email,
    password,
    mfaDescription,
    mfaOptions,
    mfaTitle,
    mfaMethod,
    userToken,
    loggedInUser,
    profileImage,
    userName,
    isMFAFromEmail,
    emailSentScenario,
  } = state;

  switch (view) {
    case View.ENTER_EMAIL:
      return (
        <EnterEmailView
          defaultEmail={email}
          onComplete={onComplete}
          onLogin={onLogin}
          signInTitle={signInTitle ?? signInTitleEmailView}
          hideApple={hideApple}
          isResetPasswordSuccess={isResetPasswordSuccess}
        />
      );
    case View.ENTER_PASSWORD:
      return (
        <EnterPasswordView
          signInTitle={signInTitle ?? signInTitlePasswordView}
          defaultEmail={email}
          userToken={userToken}
          withEmail={isNotUndefined(email)}
          sendResetEmail={sendResetEmail}
          onComplete={onComplete}
          onLogin={onLogin}
        />
      );
    case View.ENTER_CODE:
      return (
        <EnterCodeView
          email={email}
          userToken={userToken}
          onComplete={onComplete}
          onLogin={onLogin}
        />
      );
    case View.ENTER_MFA_CODE:
      return (
        <EnterMFACodeView
          email={email}
          userToken={userToken}
          onComplete={onComplete}
          onLogin={onLogin}
          mfaMethod={mfaMethod}
          mfaTitle={mfaTitle}
          password={password}
          mfaDescription={mfaDescription}
        />
      );
    case View.ENTER_MFA:
      return (
        <EnterMFAView
          email={email}
          password={password}
          userToken={userToken}
          mfaDescription={mfaDescription}
          mfaTitle={mfaTitle}
          mfaMethod={mfaMethod}
          onComplete={(result) => {
            onComplete(result);
            resetMFADescription();
            resetMFATitle();
          }}
          onLogin={onLogin}
        />
      );
    case View.CREATE_PASSWORD:
      logAssert(
        !!loggedInUser,
        "User should never be null when trying to create a password during sign in flow"
      );
      return (
        <CreatePasswordView
          loggedInUser={loggedInUser}
          onComplete={onComplete}
          onLogin={onLogin}
        />
      );
    case View.RESET_PASSWORD:
      logAssert(
        !!userToken || !!email,
        "User token or email should be defined when trying to reset a password during sign in flow"
      );
      return <ResetPasswordView userToken={userToken} emailAddress={email} />;
    case View.SSO:
      return (
        <SSOView
          email={email}
          userToken={userToken}
          onComplete={onComplete}
          onLogin={onLogin}
        />
      );
    case View.CONFIRM_PASSWORD:
      return (
        <ConfirmPasswordView
          email={email}
          profileImage={profileImage}
          userName={userName}
          onComplete={onComplete}
          onLogin={onLogin}
        />
      );
    case View.SELECT_MFA_OPTION:
      return (
        <SelectMFAOptionView
          email={email}
          userToken={userToken}
          password={password}
          mfaOptions={mfaOptions ?? []}
          onComplete={(result) => {
            onComplete(result);
            resetMFAOptions();
          }}
          onLogin={onLogin}
          isMFAFromEmail={isMFAFromEmail}
        />
      );
    case View.LOADING:
      return <LoadingView />;
    case View.AUTO_SIGN_IN_LINK_SENT:
      return (
        <AutoEmailSentWaitingView
          scenario={emailSentScenario}
          onComplete={onComplete}
          email={email}
        />
      );
    case View.MFA_OPTIONS_EMAIL_SENT:
      return (
        <MFAOptionsEmailSentWaitingView
          onComplete={onComplete}
          email={email}
          mfaTitle={mfaTitle}
          mfaDescription={mfaDescription}
          password={password}
        />
      );
    default:
      return null;
  }
};
